import { Navigate, useParams, useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { Ellie } from '../components/Ellie';
import { useFeatureFlag } from '../services/featureFlag.service';
import { FeatureFlag } from '../services/featureFlag.service';
import FeatureFlagService from '../services/featureFlag.service';
import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { EllieHeader } from '../components/EllieHeader';
import { SidePanelProvider } from '../components/chat/SidePanel';
import { SidePanelLayout } from '../components/chat/SidePanelLayout';
// Registers the 'dashboard' side-panel kind. Imported for the side effect, here
// rather than in the tool call, so the renderer exists before a historical
// thread replays a create_dashboard step.
import '../components/assistant-ui/dashboard-side-panel';
import { useIsMobileApp } from '../hooks/use-mobile';
import MobileEllie from './mobile/MobileEllie';

type AgentType = 'analyst' | 'use_case_discovery' | 'eda' | 'data_modeling' | 'ellie';

/**
 * The conversation page, for both `/ellie` and `/ellie/conversation/:id`.
 *
 * One component for both: they were separate files rendering near-identical
 * trees, which meant anything added to the conversation shell had to be added
 * twice — and the side panel was, briefly, added to only one. With no `:id` this
 * starts a new conversation, which is all the `/ellie` route ever did.
 */
export default function ElliePage({
  embedThreadId,
  showHistory: embedShowHistory = false,
  centerInputOnNewChat: embedCenterInput,
  placeholder: embedPlaceholder,
  welcomeMessage: embedWelcomeMessage,
}: {
  embedThreadId?: string;
  showHistory?: boolean;
  centerInputOnNewChat?: boolean;
  placeholder?: string;
  welcomeMessage?: string;
} = {}) {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isAiAssistantEnabled = useFeatureFlag(FeatureFlag.AI_ASSISTANT, false);
  const useCasesEnabled = useFeatureFlag(FeatureFlag.ALLOW_USECASE_MANAGEMENT, false);
  const [flagsReady, setFlagsReady] = useState(() => FeatureFlagService.getInstance().isReady);
  const isMobileApp = useIsMobileApp();
  const isEmbedded =
    typeof window !== 'undefined' && window.location.pathname === '/embed';

  // A question handed over from elsewhere — the home page's Ask Ellie box —
  // rides on history state rather than the URL, since it's a one-shot kickoff
  // and not part of the conversation's address. Captured once so it survives
  // the cleanup below.
  const [initialQuestion] = useState<string>(
    () => (location.state as { initialQuestion?: string } | null)?.initialQuestion || '',
  );

  // Strip it back off history state, so refreshing or navigating back into this
  // thread doesn't ask the same question a second time.
  useEffect(() => {
    if ((location.state as { initialQuestion?: string } | null)?.initialQuestion) {
      navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    }
  }, [location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    if (flagsReady) return;
    FeatureFlagService.getInstance().waitForInitialization().then(() => setFlagsReady(true));
  }, [flagsReady]);

  if (!flagsReady) {
    return (
      <div className="flex items-center justify-center h-screen w-full">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Flag off: Ellie isn't part of this tenant's plan. In-app, send them home —
  // the nav item and the home page's ask box are gone too, so a bookmarked
  // /ellie is the only way in and it shouldn't sit on a spinner forever. An
  // embed has no app to go home to, so it just renders nothing.
  if (!isAiAssistantEnabled) {
    return isEmbedded ? null : <Navigate to="/" replace />;
  }

  const agentOverride = searchParams.get('agent') as AgentType | null;
  const agentType: AgentType = agentOverride || (useCasesEnabled ? 'ellie' : 'analyst');

  // An embed names its thread through a prop rather than the URL; everywhere
  // else it is the route param. Undefined in both means "start a new one".
  const conversationId = embedThreadId ?? id;

  // Phones get the history rail as a sheet instead of a permanent column; see
  // MobileEllie. Embeds are excluded by useIsMobileApp (they're framed).
  if (isMobileApp) {
    return (
      <MobileEllie
        conversationId={conversationId}
        agentType={agentType}
        initialQuestion={initialQuestion}
      />
    );
  }

  // Embeds keep the unsplit layout: they are framed to a size the host chose,
  // and splitting that in two leaves neither half usable.
  if (isEmbedded) {
    return (
      <div className="h-screen w-full flex flex-col">
        {/* Embedded chat is white-labeled: no Ellie identity header, no advanced controls */}
        <div className="flex-1 min-h-0 overflow-hidden">
          {/* With history, the chat + sidebar use the full width; without it,
              the lone conversation stays centered at 3/4 width. */}
          <div className={embedShowHistory ? 'h-full w-full' : 'h-full w-3/4 mx-auto'}>
            <Ellie
              showHistory={embedShowHistory}
              agentType={agentType}
              conversationId={conversationId}
              embedded
              centerInputOnNewChat={embedCenterInput}
              placeholder={embedPlaceholder}
              welcomeMessage={embedWelcomeMessage}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <SidePanelProvider>
      <div className="h-screen w-full flex flex-col">
        <EllieHeader />
        <div className="flex-1 min-h-0 overflow-hidden">
          {/* Renders the conversation alone until a tool call asks for the
              panel, so the default layout is unchanged. */}
          <SidePanelLayout>
            {({ isSplit }) => (
              <Ellie
                // The history rail is the first thing to go when the
                // conversation is down to a third of the screen — it is
                // navigation, and the thread is the thing being read.
                showHistory={!isSplit}
                agentType={agentType}
                conversationId={conversationId}
                initialQuestion={initialQuestion}
              />
            )}
          </SidePanelLayout>
        </div>
      </div>
    </SidePanelProvider>
  );
}
